import localforage from 'localforage';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Board } from '../types';

// Configure localforage
localforage.config({
  name: 'Fibre',
  storeName: 'boards'
});

export class BoardStorage {
  private static instance: BoardStorage;
  private cache: Map<string, Board> = new Map();

  static getInstance(): BoardStorage {
    if (!BoardStorage.instance) {
      BoardStorage.instance = new BoardStorage();
    }
    return BoardStorage.instance;
  }

  clearCache(): void {
    this.cache.clear();
  }


  async getAllBoards(uid: string | null): Promise<Board[]> {
    try {
      const boards: Board[] = [];

      if (uid && uid !== 'dev_user') {
        // Fetch from Firestore
        const q = query(collection(db, `users/${uid}/boards`));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const board = doc.data() as Board;
          boards.push(board);
          this.cache.set(board.id, board);
        });
      } else {
        // Fetch from LocalForage
        await localforage.iterate<Board, void>((board) => {
          if (board && board.id) {
            boards.push(board);
            this.cache.set(board.id, board);
          }
        });
      }

      return boards.sort((a, b) => b.updatedAt - a.updatedAt);
    } catch (error) {
      console.error('Error loading boards:', error);
      return [];
    }
  }

  async getBoard(id: string, uid: string | null): Promise<Board | null> {
    try {
      // Check cache first (optional, but good for perf)
      if (this.cache.has(id)) {
        return this.cache.get(id)!;
      }

      if (uid && uid !== 'dev_user') {
        // Firestore
        const docRef = doc(db, `users/${uid}/boards`, id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const board = docSnap.data() as Board;
          this.cache.set(id, board);
          return board;
        }
        return null; // Not found in cloud
      } else {
        // LocalForage
        const board = await localforage.getItem<Board>(id);
        if (board) {
          this.cache.set(id, board);
        }
        return board;
      }
    } catch (error) {
      console.error('Error loading board:', error);
      return null;
    }
  }

  async saveBoard(board: Board, uid: string | null, force: boolean = false): Promise<void> {
    try {
      // Clone to avoid mutating the input (React state safety)
      const boardToSave = { ...board, updatedAt: Date.now() };

      // Safety guard: Don't save empty boards unless forced or it's a new board
      if (!force && this.cache.has(board.id)) {
        const cached = this.cache.get(board.id);
        if (cached) {
          if (board.nodes.length === 0 && cached.nodes.length > 0) {
            console.warn(`Blocked accidental wipe of board nodes for ${board.id}. Use force: true to override.`);
            return;
          }
          if (board.edges.length === 0 && cached.edges.length > 0) {
            console.warn(`Blocked accidental wipe of board edges for ${board.id}. Use force: true to override.`);
            return;
          }
        }
      }

      // Recursively remove undefined values for Firestore compatibility
      const cleanedBoard = this.cleanUndefined(boardToSave);

      if (uid && uid !== 'dev_user') {
        // Firestore
        await setDoc(doc(db, `users/${uid}/boards`, cleanedBoard.id), cleanedBoard);
      } else {
        // LocalForage
        await localforage.setItem(cleanedBoard.id, cleanedBoard);
      }

      this.cache.set(cleanedBoard.id, cleanedBoard);
    } catch (error) {
      console.error('Error saving board:', error);
      throw error;
    }
  }

  private cleanUndefined(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(v => (v && typeof v === 'object') ? this.cleanUndefined(v) : v);
    }
    const newObj: any = {};
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (val !== undefined) {
        if (val && typeof val === 'object' && !(val instanceof Date)) {
          newObj[key] = this.cleanUndefined(val);
        } else {
          newObj[key] = val;
        }
      }
    });
    return newObj;
  }

  async deleteBoard(id: string, uid: string | null): Promise<void> {
    try {
      if (uid && uid !== 'dev_user') {
        // Firestore
        await deleteDoc(doc(db, `users/${uid}/boards`, id));
      } else {
        // LocalForage
        await localforage.removeItem(id);
      }

      this.cache.delete(id);
    } catch (error) {
      console.error('Error deleting board:', error);
      throw error;
    }
  }

  async duplicateBoard(id: string, newTitle: string, uid: string | null): Promise<Board> {
    const originalBoard = await this.getBoard(id, uid);
    if (!originalBoard) {
      throw new Error('Board not found');
    }

    const newBoard: Board = {
      ...originalBoard,
      id: `board_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: newTitle,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    await this.saveBoard(newBoard, uid);
    return newBoard;
  }
}